import { yupResolver } from "@hookform/resolvers/yup";
import { LoadingButton } from '@mui/lab';
import { Dialog, DialogActions, DialogContent, Grid, LinearProgress } from '@mui/material';
import Box from '@mui/material/Box';
import { makeStyles } from '@mui/styles';
import { DataGrid } from "@mui/x-data-grid";
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { IndividualService } from '../../../Component/Api/Individual/IndividualService';
import AppAccordion from '../../../Component/Common/Components/Accordion/AppAccordion';
import AppCheckbox from '../../../Component/Common/Components/FormValidationInputs/AppCheckbox';
import { AppDatepicker } from '../../../Component/Common/Components/FormValidationInputs/AppDatepicker';
import AppSelectList from '../../../Component/Common/Components/FormValidationInputs/AppSelectList';
import AppTextInput from '../../../Component/Common/Components/FormValidationInputs/AppTextInput';
import { genderList, provinceList, raceList } from '../../../Component/Common/Constants/Lists/DropdownListOptions';
import { SkeletonLoading } from '../../../Component/Common/SkeletonLoading/SkeletonLoading';
import { Individual } from '../../../Component/Model/Dto/Individual';
import { defaultObject } from "../../../Component/Model/helpers";
import { CustomDialogTitle } from "../../Common/Custom/CustomDialogTitle";
import { individualSchema } from "../../Common/Individual/IndividualValidation";
import { aggregateIndividualColumns, annualizedIndividualColumns, individualSalaryColumns } from "./IndividualCentralDataGridColumns";
import { UserActivityService } from "../../../Component/Api/UserActivity/UserActivityService";
import { CDMSummariesSalariesSection } from "../../../Component/Common/Constants/UserActivityTrackingSections";
import { UserActivityActionType } from "../../../Component/Model/Enum/BEE123Enums";

const useStyles = makeStyles({
  root: {
    "& .row--0": {
      backgroundColor: "#bdd7ee" //actual
    },
    "& .row--1": {
      backgroundColor: "white" //input
    },
    "& .row--2": {
      backgroundColor: "#e7e6e6" //percentage
    },
    "& .row--3": {
      backgroundColor: "#d6dce4" //headcountValue
    }
  }
});

interface IProps {
  id: number;
  scorecardId: string;
  showIndividualSalariesModal: boolean;
  scorecardType: string;
  closeIndividualSalariesModal: () => void;
}

export const IndividualCentralDataSalaryModal = (props: IProps) => {
  const classes = useStyles();
  const { control, reset, handleSubmit, formState: { isSubmitting }, setValue } = useForm({
    mode: 'all',
    resolver: yupResolver(individualSchema)
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [individualSalaryData, setIndividualSalaryData] = useState<Individual.SalaryPerYearDto[]>([]);
  const [individual, setIndividual] = useState<Individual.IndividualDto>();

  const getEmployeeData = async (id: number) => {
    if (id === undefined || id === 0) return;
    try {
      const request: Individual.GetRequest = {
        id: id,
        scorecardId: props.scorecardId,
        LinkedType: undefined,
        InterventionId: undefined,
        filterVAStartDate: undefined,
        filterVAEndDate: undefined
      }
      setIsLoading(true);
      const result = await IndividualService.GetIndividualByID(request);
      var individualData = result.data as Individual.IndividualDto;

      reset(individualData);
      setIndividual(individualData);
      GetSalaries(result.data);
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  }

  const GetSalaries = async (individual: Individual.IndividualDto) => {
    try {
      setIndividualSalaryData(individual.salaryPerYears);
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  }

  const hasIndividualSalaryData = (): boolean => {
    return individualSalaryData && individualSalaryData.length > 0;
  }

  const handleDataSubmit = async (data: FieldValues) => {
    try {
      let individualSalaryDataOut: Individual.SalaryDtoOut[] = [];

      individualSalaryData.forEach(individualSalaryDatayear => {
        individualSalaryDatayear.salaryDatas.forEach(salaryMonth => {
          individualSalaryDataOut.push(salaryMonth);
        });
      });
      data.salariesPerYears = individualSalaryDataOut;

      let updateIndividualDto = {
        individualId: individual?.individualId,
        scorecardId: individual?.scorecardId!,
        firstName: data.firstName,
        lastName: data.lastName,
        identificationNumber: data.identificationNumber,
        raceId: data.raceId,
        gender: data.gender,
        disabled: data.disabled,
        userRegistrationId: individual?.userRegistrationId,
        active: individual?.active,
        sourceScorecardId: individual?.sourceScorecardId,
        hasBeenChangedFromSource: individual?.hasBeenChangedFromSource,
        sourceIndividualId: individual?.sourceIndividualId,
        hashedIdentificationNumber: individual?.hashedIdentificationNumber,
        numberOfSkillsDevInterventions: individual?.numberOfSkillsDevInterventions,
        totalSkillsDevSpend: individual?.totalSkillsDevSpend,
        isOfficeBased: individual?.isOfficeBased,
        professionalRegistrationDate: individual?.professionalRegistrationDate,
        dateOfBirth: new Date(data.dateOfBirth),
        votingRights: individual?.votingRights,
        province: data.province,
        isCandidate: individual?.isCandidate,
        isYes: data.isYes,
        isYesAbsorbed: data.isYesAbsorbed,
        isMilitaryVeteran: individual?.isMilitaryVeteran,
        employeeCode: data.employeeCode,
        employeeDatas: individual?.employeeDatas,
        salaries: individualSalaryDataOut,
        scorecardSalaryYear: individual?.scorecardSalaryYear,
        SampledIndividualViewModel: null
      } as Individual.IndividualUpdateDto;

      const result = await IndividualService.SaveIndividual(updateIndividualDto);
      UserActivityService.createActivityLog({
        section: CDMSummariesSalariesSection,
        actionType: UserActivityActionType.Update,
        actionDescription: "Update employee details",
        requestUrl: window.location.href
      });
      toast.success(`Employee updated successfully`);
      props.closeIndividualSalariesModal();
    } catch (error) {
      console.log(error)
    }
  }

  useEffect(() => {
    getEmployeeData(props.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.showIndividualSalariesModal, props.id])

  const processIndividualSalaryUpdate = async (newRow: Individual.Salary, oldRow: Individual.Salary) => {
    if (_.isEqual(newRow, oldRow))
      return newRow;

    try {
      let data = individualSalaryData;
      let year = new Date(newRow.displayDate).getFullYear();
      let idxBlock = data.findIndex(x => x.year === year);
      let block = data[idxBlock];
      if (!block)
        return oldRow;

      let idxSalary = _.findIndex(block.salaryDatas, x => x.displayDate === newRow.displayDate);
      let salaries = block.salaryDatas
      let salary = { ...salaries[idxSalary], salary: newRow.salary, variable: "N/A", fixed: "N/A" } || defaultObject;
      salaries = salaries.filter(x => x.displayDate !== newRow.displayDate);
      salaries.push(salary);
      salaries = _.sortBy(salaries, x => new Date(x.displayDate));
      block.salaryDatas = salaries;
      data.splice(idxBlock, 1, block);
      setIndividualSalaryData(data);
      return newRow;

    } catch (error: any) {
      toast.error(error.response.data);
      return oldRow;
    } finally { }
  }

  const renderIndividualSalaries = () => {
    return hasIndividualSalaryData() ? (individualSalaryData.sort((a, b) => b.year - a.year).map((blockItem: Individual.SalaryPerYearDto, index) => {
      return (
        <AppAccordion
          expandOnDefault={true}
          label={blockItem.year.toString()}
          key={`acc-year-${blockItem.year}`}>
          {
            renderIndividualSalariesForScorecardPeriod(blockItem.year, blockItem.salaryDatas)
          }
        </AppAccordion>
      )
    })) : (<></>);
  }

  const renderIndividualSalariesForScorecardPeriod = (year: number, salaries: Individual.Salary[]) => {
    let totalsCtc = salaries.reduce((sum, current) => sum + Number(current.salary), 0);
    let summedCTCValue = 0;
    let ctcCount = 0;
    salaries.forEach(function (salary) {
      summedCTCValue += Number(salary.salary);
      ctcCount += Number(salary.salary > 0 ? 1 : 0);
    });

    const annualizedCtc = ctcCount > 0 ? (summedCTCValue / ctcCount) * 12 : 0;
    var idCount = 0;
    let modifiedSalaries = salaries.map((salary) => ({
      ...salary,
      id: idCount++
    }));
    return salaries && salaries.length > 0 ?
      (<div>
        <Grid container mb={1}>
          <Grid item xs={12}>
            <DataGrid
              key={`salaries-grd-${year}`}
              headerHeight={70}
              className={classes.root}
              disableSelectionOnClick
              autoHeight
              hideFooter
              disableColumnFilter
              disableColumnMenu
              disableColumnSelector
              rowHeight={25}
              rows={modifiedSalaries}
              columns={individualSalaryColumns}
              editMode="row"
              isCellEditable={(params) => (true)}
              experimentalFeatures={{ newEditingApi: true }}
              processRowUpdate={processIndividualSalaryUpdate}
              components={{
                LoadingOverlay: LinearProgress,
              }}
              loading={isLoading}
            />
            <DataGrid
              key={`salaries-totals-grd-${year}`}
              headerHeight={0}
              className={classes.root}
              disableSelectionOnClick
              autoHeight
              hideFooter
              disableColumnFilter
              disableColumnMenu
              disableColumnSelector
              rowHeight={30}
              rows={[
                { id: 1, displayDates: 'TOTAL', salary: totalsCtc, fixed: "N/A", variable: "N/A" }
              ]}
              columns={aggregateIndividualColumns}
              isCellEditable={(params) => (false)}
              components={{
                LoadingOverlay: LinearProgress,
              }}
              loading={isLoading}
            />
            <DataGrid
              key={`salaries-totals-grd-${year}`}
              headerHeight={0}
              className={classes.root}
              disableSelectionOnClick
              autoHeight
              hideFooter
              disableColumnFilter
              disableColumnMenu
              disableColumnSelector
              rowHeight={30}
              rows={[
                { id: 1, displayDates: 'Annualized', salary: annualizedCtc, fixed: "N/A", variable: 'N/A' }
              ]}
              columns={annualizedIndividualColumns}
              isCellEditable={(params) => (true)}
              components={{
                LoadingOverlay: LinearProgress,
              }}
              loading={isLoading}
            />
          </Grid>
        </Grid>
      </div>)
      :
      (<div>no salary rows for year</div>);
  }

  return (
    <Dialog disableEnforceFocus open={props.showIndividualSalariesModal} onClose={() => { props.closeIndividualSalariesModal() }} aria-labelledby="form-dialog-title" maxWidth='lg'>
      <CustomDialogTitle id="individual-dialog-title" onClose={() => props.closeIndividualSalariesModal()}>
        {'View/Edit Employee & Salary Details'}
      </CustomDialogTitle>
      <DialogContent>
        {isLoading && <SkeletonLoading formLoading FieldAmount={6} width={350} />}
        {!isLoading &&
          <Box>
            <AppAccordion expandOnDefault={true} label={'Employee Data'}>
              <form id='IndividualForm' onSubmit={handleSubmit(handleDataSubmit)}>
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={12} mt={1}>
                    <AppTextInput control={control} name="identificationNumber" label="ID Number" />
                  </Grid>
                  <Grid item xs={12} sm={12}>
                    <AppTextInput control={control} name="employeeCode" label="Employee Code" />
                  </Grid>
                  <Grid item xs={12} sm={12}>
                    <AppTextInput control={control} name="firstName" label="Name" />
                  </Grid>
                  <Grid item xs={12} sm={12}>
                    <AppTextInput control={control} name="lastName" label="Surname" />
                  </Grid>
                  <Grid item xs={12} sm={12}>
                    <AppSelectList control={control} name="gender" label="Gender" items={genderList} />
                  </Grid>
                  <Grid item xs={12} sm={12}>
                    <AppSelectList control={control} name="raceId" label="Race" items={raceList} />
                  </Grid>
                  <Grid item xs={12} sm={12}>
                    <AppSelectList control={control} name="province" label="Province" items={provinceList} />
                  </Grid>
                  <Grid item xs={12} sm={12}>
                    <AppDatepicker control={control} name="dateOfBirth" label="Date Of Birth" inputFormat='yyyy/MM/dd' />
                  </Grid>
                  <Grid item xs={12} sm={12}>
                    <AppCheckbox control={control} name='disabled' label='Disabled' disabled={false} />
                  </Grid>
                  <Grid item xs={12} sm={12}>
                    <AppCheckbox control={control} name='isYes' label='Y.E.S. Employee' disabled={false} />
                  </Grid>
                  <Grid item xs={12} sm={12}>
                    <AppCheckbox control={control} name='isYesAbsorbed' label='Y.E.S. Absorbed' disabled={false} />
                  </Grid>
                </Grid>
              </form>
            </AppAccordion>
            <Box>
              <div>{renderIndividualSalaries()}</div>
            </Box>
          </Box >}
      </DialogContent>
      <DialogActions>
        <LoadingButton loading={isSubmitting} variant='contained' form='IndividualForm' type="submit" color='primary'>
          Save
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}
